import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { Alert, AppBar, Toolbar, Typography, Button, Box, Tabs, Tab } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { logout } from '../../store/slices/authSlice';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { organizationService } from '../../services/organizationService';
import { authService } from '../../services/authService';

const NotificationContext = createContext({
  showNotification: () => {},
});

export const useNotification = () => useContext(NotificationContext);

const topTabs = [
  { label: 'Обращения', path: '/appeals' },
  { label: 'Заявки', path: '/' },
];

const subNavItems = [
  { label: 'Заявки', path: '/' },
  { label: 'Виды заявок', path: '/services' },
  { label: 'Исполнители', path: '/masters' },
];

const getOrganizationName = (organization) => {
  if (typeof organization === 'string') {
    return organization;
  }

  return organization?.name || organization?.organizationName || '';
};

const getDispatcherLogin = (profile) => {
  if (typeof profile?.login !== 'string') return '';
  return profile.login.trim();
};

const Layout = ({ children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const [organizationName, setOrganizationName] = useState('');
  const [dispatcherLogin, setDispatcherLogin] = useState('');
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const showNotification = useCallback((message, severity = 'info') => {
    setNotification({
      open: true,
      message: typeof message === 'string' && message ? message : 'Произошла ошибка',
      severity,
    });
  }, []);

  const hideNotification = useCallback(() => {
    setNotification((prev) => ({ ...prev, open: false }));
  }, []);

  useEffect(() => {
    if (!notification.open) return undefined;
    const timeoutMs = notification.severity === 'error' ? 10000 : 4000;
    const timer = setTimeout(hideNotification, timeoutMs);
    return () => clearTimeout(timer);
  }, [notification.open, notification.message, notification.severity, hideNotification]);

  useEffect(() => {
    if (!user) {
      setOrganizationName('');
      setDispatcherLogin('');
      return undefined;
    }

    let cancelled = false;

    const loadHeader = async () => {
      const [organization, profile] = await Promise.all([
        organizationService.getCurrentOrganization().catch(() => null),
        authService.getProfile().catch(() => null),
      ]);

      if (cancelled) return;
      setOrganizationName(getOrganizationName(organization));
      setDispatcherLogin(getDispatcherLogin(profile));
    };

    loadHeader();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const isAppealsTab = location.pathname === '/appeals';
  const topTabValue = isAppealsTab ? 0 : 1;
  const activeSubNav = subNavItems.findIndex(
    (item) => location.pathname === item.path
  );

  return (
    <NotificationContext.Provider value={{ showNotification }}>
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      {/* Top AppBar */}
      <AppBar position="static" elevation={1}>
        <Toolbar variant="dense">
          <Typography
            variant={isMobile ? 'body2' : 'subtitle1'}
            component="div"
            sx={{ fontWeight: 600, mr: 3 }}
          >
            Диспетчер заявок{organizationName ? ` «${organizationName}»` : ''}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          {user && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {dispatcherLogin && (
                <Typography
                  variant="body2"
                  noWrap
                  sx={{ mr: isMobile ? 1 : 2, maxWidth: isMobile ? 120 : 240 }}
                >
                  {dispatcherLogin}
                </Typography>
              )}
              <Button
                color="inherit"
                onClick={handleLogout}
                size="small"
                sx={{ textTransform: 'none' }}
              >
                Выйти
              </Button>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {notification.open && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            px: 2,
            py: 1,
            bgcolor: 'background.paper',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Alert
            severity={notification.severity}
            onClose={hideNotification}
            sx={{ maxWidth: 640, width: '100%' }}
          >
            {notification.message}
          </Alert>
        </Box>
      )}

      {/* Top-level tabs: Обращения / Заявки */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Tabs
          value={topTabValue}
          onChange={(_, newValue) => navigate(topTabs[newValue].path)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {topTabs.map((tab) => (
            <Tab
              key={tab.path}
              label={tab.label}
              sx={{ textTransform: 'none', fontWeight: 500 }}
            />
          ))}
        </Tabs>
      </Box>

      {/* Secondary navigation under "Заявки" tab */}
      {!isAppealsTab && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
          <Box sx={{ display: 'flex', gap: 0.5, px: 2, py: 0.5 }}>
            {subNavItems.map((item, index) => {
              const active = activeSubNav === index;
              return (
                <Button
                  key={item.path}
                  size="small"
                  onClick={() => navigate(item.path)}
                  sx={{
                    textTransform: 'none',
                    fontWeight: active ? 600 : 400,
                    color: active ? 'primary.main' : 'text.secondary',
                    borderBottom: active ? '2px solid' : '2px solid transparent',
                  borderColor: active ? 'primary.main' : 'transparent',
                    borderRadius: 0,
                    px: 1.5,
                    py: 0.5,
                  }}
                >
                  {item.label}
                </Button>
              );
            })}
          </Box>
        </Box>
      )}

      {/* Page content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          overflow: 'hidden',
        }}
      >
        {children}
      </Box>
    </Box>
    </NotificationContext.Provider>
  );
};

export default Layout;
